// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChakraProvider, Box, Flex, useBreakpointValue, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, IconButton } from '@chakra-ui/react';
import { FiMenu } from 'react-icons/fi';
import { theme } from './theme/theme';
import { AuthContext } from './contexts/AuthContext';
import { apiService } from './services/apiService';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Enrollment from './pages/Enrollment';
import EnrollmentValidation from './pages/EnrollmentValidation';
import BiblicalReading from './pages/BiblicalReading';
import WhatsAppAdmin from './pages/WhatsAppAdmin';
import Comptabilite from './pages/Comptabilite';
import MemberContributions from './pages/MemberContributions';
import MemberMerge from './pages/MemberMerge';
import PaymentReturn from './pages/PaymentReturn';
import SumUpCheckout from './pages/SumUpCheckout';
import Annuaire from './pages/Annuaire';
import ImportPaiements from './pages/ImportPaiements';
import PWAInstallBanner from './components/PWAInstallBanner';

// Components
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import MemberEdit from './pages/MemberEdit';
import MemberCreate from './pages/MemberCreate';
import MemberProfile from './pages/MemberProfile';
import Contributions from './pages/Contributions';
import Worship from './pages/Worship';
import BlogManagement from './pages/BlogManagement';
import PublicBlog from './pages/PublicBlog';
import Settings from './pages/Settings';
import Expenses from './pages/Expenses';
import RoleManagement from './pages/RoleManagement';
import ContactPasteur from './pages/ContactPasteur';
import MessagesPasteur from './pages/MessagesPasteur';
import BibleInteractive from './pages/BibleInteractive';
import Statistiques from './pages/Statistiques';
import BlogCategories from './pages/BlogCategories';
import Help from './pages/Help';

// Enregistre chaque changement de page pour les statistiques de visites
// (non bloquant : un échec n'affecte jamais la navigation de l'utilisateur)
function VisiteTracker() {
  const location = useLocation();
  useEffect(() => {
    apiService.makeRequest('/analytics/visite', {
      method: 'POST',
      body: JSON.stringify({ chemin: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [isLoading, setIsLoading] = useState(true);

  const authValue = {
    user,
    token,
    login: (token, userData) => {
      localStorage.setItem('adminToken', token);
      setToken(token);
      setUser(userData);
    },
    logout: () => {
      localStorage.removeItem('adminToken');
      setToken(null);
      setUser(null);
    },
    isAuthenticated: !!token
  };

  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Token invalide:', error);
          authValue.logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isLoading) {
    return <Box>Chargement...</Box>;
  }

  return (
    <ChakraProvider theme={theme}>
      <AuthContext.Provider value={authValue}>
        <PWAInstallBanner />
        <Router>
          <VisiteTracker />
          <Routes>
            {/* Routes publiques - accessibles sans connexion */}
            <Route path="/" element={<PublicBlog />} />
            <Route path="/blog" element={<PublicBlog />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/enrollment" element={<Enrollment />} />

            {/* Routes protégées - nécessitent une connexion */}
            {authValue.isAuthenticated ? (
              <Route
                path="/*"
                element={<AppLayout />}
              />
            ) : (
              <Route path="*" element={<Navigate to="/blog" />} />
            )}
          </Routes>
        </Router>
      </AuthContext.Provider>
    </ChakraProvider>
  );
}

function AppLayout() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Flex minHeight="100vh">
      {/* Sidebar desktop */}
      {!isMobile && <Sidebar />}

      {/* Drawer mobile */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay />
          <DrawerContent maxW="260px">
            <DrawerCloseButton zIndex={10} />
            <Sidebar onNavigate={onClose} />
          </DrawerContent>
        </Drawer>
      )}

      <Box flex="1" bg="#f2f5f2" ml={isMobile ? '0' : '250px'} minW={0}>
        <Header onMenuClick={isMobile ? onOpen : undefined} isMobile={isMobile} />
        <Box p={{ base: 3, md: 6 }}>
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<MemberProfile />} />
            <Route path="/contributions/payment-return" element={<PaymentReturn />} />
            <Route path="/sumup-checkout" element={<SumUpCheckout />} />
            <Route path="/contributions/me" element={<MemberContributions />} />
            <Route path="/annuaire" element={<Annuaire />} />
            <Route path="/members" element={<ProtectedRoute roles={['pasteur', 'president']}><Members /></ProtectedRoute>} />
            <Route path="/members/new" element={<ProtectedRoute roles={['pasteur', 'president']}><MemberCreate /></ProtectedRoute>} />
            <Route path="/members/doublons" element={<ProtectedRoute roles={['pasteur', 'president']}><MemberMerge /></ProtectedRoute>} />
            <Route path="/members/:id/edit" element={<ProtectedRoute roles={['pasteur', 'president']}><MemberEdit /></ProtectedRoute>} />
            <Route path="/contributions" element={<ProtectedRoute roles={['pasteur', 'president', 'tresorier', 'commissaire', 'secretaire']}><Contributions /></ProtectedRoute>} />
            <Route path="/contributions/import" element={<ProtectedRoute adminOnly={true}><ImportPaiements /></ProtectedRoute>} />
            <Route path="/worship" element={<ProtectedRoute roles={['pasteur', 'president', 'tresorier', 'commissaire']}><Worship /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute roles={['pasteur', 'president', 'editeur']}><BlogManagement /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute adminOnly={true}><Settings /></ProtectedRoute>} />
            <Route path="/admin/roles" element={<ProtectedRoute adminOnly={true}><RoleManagement /></ProtectedRoute>} />
            <Route path="/contact-pasteur" element={<ProtectedRoute><ContactPasteur /></ProtectedRoute>} />
            <Route path="/admin/messages-pasteur" element={<ProtectedRoute roles={['pasteur']}><MessagesPasteur /></ProtectedRoute>} />
            <Route path="/bible" element={<ProtectedRoute><BibleInteractive /></ProtectedRoute>} />
            <Route path="/admin/statistiques" element={<ProtectedRoute adminOnly={true}><Statistiques /></ProtectedRoute>} />
            <Route path="/admin/blog-categories" element={<ProtectedRoute><BlogCategories /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute roles={['pasteur', 'president', 'tresorier', 'commissaire']}><Expenses /></ProtectedRoute>} />
            <Route path="/admin/biblical-reading" element={<ProtectedRoute roles={['pasteur', 'president']}><BiblicalReading /></ProtectedRoute>} />
            <Route path="/admin/whatsapp" element={<ProtectedRoute roles={['pasteur', 'president']}><WhatsAppAdmin /></ProtectedRoute>} />
            <Route path="/admin/enrollment-validation" element={<ProtectedRoute roles={['pasteur', 'president']}><EnrollmentValidation /></ProtectedRoute>} />
            <Route path="/comptabilite" element={<ProtectedRoute><Comptabilite /></ProtectedRoute>} />
            <Route path="/aide" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Box>
      </Box>
    </Flex>
  );
}

export default App;
